import { useParams, useNavigate, useSearchParams } from 'react-router';
import { Text, Button, Badge, Spinner, makeStyles, tokens } from '@fluentui/react-components';
import { ArrowLeft24Regular } from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, XAxis, YAxis, CartesianGrid, ReferenceLine,
} from 'recharts';
import { fetchLote, fetchLoteActivities, fetchLoteCosts, fetchLoteMovements } from '../api/lotes.api';
import { fetchModules } from '../api/modules.api';
import { fetchLoteBudget, fetchNdvi, fetchNdviHistory } from '../api/dashboard.api';
import { fetchSaleOrders } from '../api/sales.api';
import { fetchAnomalies, type AnomalySeverity } from '../api/production.api';
import { ActivityFeed } from '../components/shared/ActivityFeed';
import { FilterPanel, type FilterDimension } from '../components/shared/FilterPanel';
import { BarLineChart } from '../components/shared/charts/BarLineChart';
import { SubTabs } from '../components/shared/SubTabs';
import { LoteSystemActivity } from '../components/lote/LoteSystemActivity';
import { useT } from '../i18n';
import { useLocaleSettings } from '../hooks/useLocaleSettings';
import { useIntlFormat } from '../hooks/useIntlFormat';
import { useTenantStore } from '../store/tenant.store';
import { useUrlFilters } from '../hooks/useUrlFilters';
import { useNdviSettings } from '../hooks/useNdviSettings';
import { isNdviDisabledError } from '../api/ndvi-settings.api';
import type { LoteMovement } from '../types/api.types';

const SEVERITY_BORDER: Record<AnomalySeverity, string> = {
  BAJA: tokens.colorNeutralStroke2,
  MEDIA: tokens.colorPaletteYellowBorder2,
  ALTA: tokens.colorPaletteMarigoldBorder2,
  CRITICA: tokens.colorPaletteRedBorder2,
};

const COLORS = ['#0078d4', '#107c10', '#d83b01', '#8764b8', '#038387', '#ca5010'];

/** Pestañas del detalle del lote; la activa vive en `?tab=` de la URL. */
const LOTE_TABS = ['resumen', 'mapa', 'actividades', 'finanzas'] as const;
type LoteTab = (typeof LOTE_TABS)[number];

const useStyles = makeStyles({
  root:   { display: 'flex', flexDirection: 'column', gap: '24px' },
  header: { display: 'flex', alignItems: 'center', gap: '12px' },
  zoneList: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' },
  zoneGroup: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px' },
  section: { display: 'flex', flexDirection: 'column', gap: '12px' },
  meta: {
    display: 'flex',
    gap: '24px',
    padding: '16px 20px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
  },
  metaItem:  { display: 'flex', flexDirection: 'column', gap: '4px' },
  metaLabel: { fontSize: '11px', color: tokens.colorNeutralForeground3, textTransform: 'uppercase' },
  headerCost: {
    marginLeft: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '2px',
  },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius:    tokens.borderRadiusMedium,
    padding:         '16px 20px',
    display:         'flex',
    flexDirection:   'column',
    gap:             '10px',
  },
  budgetRow: {
    display:       'flex',
    justifyContent: 'space-between',
    fontSize:      '13px',
    padding:       '6px 0',
    borderBottom:  `1px solid ${tokens.colorNeutralStroke2}`,
  },
  budgetTotal: {
    display:        'flex',
    justifyContent: 'space-between',
    fontWeight:     '700',
    fontSize:       '14px',
    paddingTop:     '8px',
  },
  // NDVI
  ndviCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius:    tokens.borderRadiusMedium,
    padding:         '16px 20px',
    display:         'flex',
    flexDirection:   'column',
    gap:             '12px',
  },
  ndviStats: {
    display:             'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap:                 '12px',
  },
  ndviStatItem: {
    display:         'flex',
    flexDirection:   'column',
    gap:             '2px',
    alignItems:      'center',
    textAlign:       'center',
    padding:         '10px 8px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius:    tokens.borderRadiusSmall,
  },
  ndviStatValue: {
    fontSize:   '22px',
    fontWeight: '700',
  },
  ndviStatLabel: {
    fontSize: '11px',
    color:    tokens.colorNeutralForeground3,
    textTransform: 'uppercase' as const,
  },
  ndviImage: {
    width:        '100%',
    maxHeight:    '200px',
    objectFit:    'cover' as const,
    borderRadius: tokens.borderRadiusSmall,
  },
  ndviBadge: {
    display:    'flex',
    gap:        '8px',
    alignItems: 'center',
    fontSize:   '12px',
    color:      tokens.colorNeutralForeground3,
  },
  ndviPlaceholder: {
    fontSize:  '13px',
    color:     tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  // Fase B — capas de mapa / ortomosaico / historial de precio / riesgos
  layerPlaceholder: {
    padding: '24px 16px',
    textAlign: 'center' as const,
    color: tokens.colorNeutralForeground3,
    fontSize: '13px',
    fontStyle: 'italic',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusSmall,
  },
  priceStat: { fontSize: '13px', color: tokens.colorNeutralForeground3 },
  riskRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
    padding: '10px 14px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    borderLeft: '4px solid transparent',
  },
  riskInfo: { display: 'flex', flexDirection: 'column', gap: '2px' },
  riskTitle: { fontWeight: tokens.fontWeightSemibold, fontSize: '13px' },
  riskMeta: { fontSize: '11px', color: tokens.colorNeutralForeground3 },
  // "Datos del lote" — FilterPanel (gap FieldData)
  movementsList: { display: 'flex', flexDirection: 'column', gap: '6px' },
  movementRow: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '8px 16px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
  },
});

/** Convierte NDVI mean (−1..1) a color semáforo */
function ndviColor(mean: number | null): string {
  if (mean === null) return tokens.colorNeutralForeground3;
  if (mean >= 0.5)   return '#107c10';
  if (mean >= 0.3)   return '#ca5010';
  return '#d83b01';
}

/** Etiqueta de salud basada en NDVI mean */
function ndviHealthLabel(mean: number | null, ld: Record<string, string>): string {
  if (mean === null) return '';
  if (mean >= 0.5)   return ld.ndviHealthGood;
  if (mean >= 0.3)   return ld.ndviHealthMid;
  return ld.ndviHealthLow;
}

export function LoteDetailPage() {
  const { loteId, tenantId } = useParams<{ loteId: string; tenantId: string }>();
  const navigate   = useNavigate();
  const s          = useStyles();
  const t          = useT();
  const ld         = t.loteDetail;
  const { formatCurrency, formatDate } = useLocaleSettings();
  const { fmtDayMonth } = useIntlFormat();
  const { displayName } = useTenantStore();
  const priceSubtitle = displayName
    ? ld.priceSubtitleWithCompany.replace('{company}', displayName)
    : ld.priceSubtitle;

  const { data: lote, isLoading: loadingLote } = useQuery({
    queryKey: ['lote', loteId],
    queryFn:  () => fetchLote(loteId!),
    enabled:  !!loteId,
  });

  // Base sin filtrar — solo para construir la lista de opciones de "Usuario"
  // del FilterPanel (los dropdowns de filtro deben listar todos los valores
  // posibles, no solo los que sobreviven al filtro activo).
  // Zonas del lote (zona → subzonas) para navegar de lote a zona con clic.
  const { data: loteModules = [] } = useQuery({
    queryKey: ['lote-modules', loteId],
    queryFn:  () => fetchModules({ loteId }),
    enabled:  !!loteId,
  });
  const topZones = loteModules.filter(m => !m.parentModuleId);

  const { data: baseActivities = [] } = useQuery({
    queryKey: ['loteActivitiesBase', loteId],
    queryFn:  () => fetchLoteActivities(loteId!),
    enabled:  !!loteId,
  });

  const { data: movements = [] } = useQuery({
    queryKey: ['loteMovements', loteId],
    queryFn:  () => fetchLoteMovements(loteId!),
    enabled:  !!loteId,
  });

  const { filters, setFilter, clearFilter, clearAll } = useUrlFilters(['type', 'userId', 'from', 'to'] as const);
  const dateRangeValue = { from: filters.from[0], to: filters.to[0] };

  // Pestaña activa en `?tab=` (conserva los filtros de Datos del lote en la URL).
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const activeTab: LoteTab = (LOTE_TABS as readonly string[]).includes(rawTab ?? '')
    ? (rawTab as LoteTab)
    : 'resumen';
  const selectTab = (value: string) =>
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', value);
      return next;
    }, { replace: true });

  const { data: activities = [], isLoading: loadingActs } = useQuery({
    queryKey: ['loteActivities', loteId, filters.type, filters.userId, filters.from, filters.to],
    queryFn:  () => fetchLoteActivities(loteId!, {
      type:   filters.type,
      userId: filters.userId[0],
      from:   filters.from[0],
      to:     filters.to[0],
    }),
    enabled:  !!loteId,
  });

  const { data: costs } = useQuery({
    queryKey: ['loteCosts', loteId],
    queryFn:  () => fetchLoteCosts(loteId!),
    enabled:  !!loteId,
  });

  const { data: budgets = [] } = useQuery({
    queryKey: ['loteBudget', loteId],
    queryFn:  () => fetchLoteBudget(loteId!),
    enabled:  !!loteId,
  });

  // `ndvi.enabled` (empresa/rancho, apagado por defecto): si se sabe apagado no se
  // llama al backend; si no se pudo leer el ajuste, el 403 ndvi_disabled lo resuelve.
  const ndviSettings = useNdviSettings();
  const ndviEnabledSetting = ndviSettings.isEnabledForRancho(lote?.ranchoId);
  const ndviFetchAllowed = !ndviSettings.isLoading && !loadingLote && ndviEnabledSetting !== false;

  const { data: ndvi, isLoading: loadingNdvi, error: ndviError } = useQuery({
    queryKey:  ['loteNdvi', loteId],
    queryFn:   () => fetchNdvi(loteId!),
    enabled:   !!loteId && ndviFetchAllowed,
    staleTime: 6 * 60 * 60 * 1000,
    retry:     false,
  });
  const ndviDisabled = ndviEnabledSetting === false || isNdviDisabledError(ndviError);

  const { data: ndviHistory } = useQuery({
    queryKey:  ['loteNdviHistory', loteId],
    queryFn:   () => fetchNdviHistory(loteId!),
    enabled:   !!loteId && ndviFetchAllowed && !ndviDisabled && !!ndvi && !ndvi.noBoundary && !ndvi.noApiKey,
    staleTime: 6 * 60 * 60 * 1000,
    retry:     false,
  });

  // Fase B — capas de mapa (NDVI ya existía; Ortomosaico es un placeholder
  // honesto, no hay pipeline real todavía).
  const mapLayer: 'ndvi' | 'ortho' = 'ndvi';

  // Fase B — historial de precio: aproximado por cultivo (no hay vínculo
  // real venta→parcela en el modelo de datos hoy, ver i18n priceSubtitle).
  const { data: saleOrders = [] } = useQuery({
    queryKey: ['loteSaleOrdersForPrice'],
    queryFn:  fetchSaleOrders,
  });

  // Fase B — riesgos de la parcela: anomalías abiertas tenant-wide, filtradas
  // por loteId en memoria (mismo patrón que RiesgosPage, Fase C).
  const { data: anomalies = [] } = useQuery({
    queryKey: ['loteAnomalies'],
    queryFn:  () => fetchAnomalies({ status: 'ABIERTA' }),
  });
  const loteRisks = anomalies.filter(a => a.loteId === loteId);

  const latestBudget = budgets[0] ?? null;

  const activityTypesMap = t.activityTypes as Record<string, string>;
  const pieData = costs
    ? Object.entries(costs.byCategory).map(([key, value]) => ({
        name:  activityTypesMap[key] ?? key,
        value,
      }))
    : [];

  // ── "Datos del lote" — FilterPanel (gap FieldData) ─────────────────────────
  // Tipo solo aplica a actividades (los movimientos de bodega no comparten el
  // mismo vocabulario de tipo, IN/OUT vs. RIEGO/COSECHA/...) — con un filtro
  // de tipo activo, los movimientos se ocultan del todo en vez de mostrarse
  // sin filtrar, para no confundir "esto no tiene tipo" con "esto coincide".
  const userOptionsMap = new Map<string, string>();
  baseActivities.forEach(a => userOptionsMap.set(a.recordedBy, a.recordedByName ?? a.recordedBy));
  movements.forEach((m: LoteMovement) => {
    if (!userOptionsMap.has(m.requestedBy)) userOptionsMap.set(m.requestedBy, m.requestedBy);
  });
  const userOptions = Array.from(userOptionsMap.entries()).map(([value, label]) => ({ value, label }));

  const typeOptions = Object.keys(activityTypesMap).map(value => ({ value, label: activityTypesMap[value] }));

  const filteredMovements = filters.type.length > 0 ? [] : movements.filter((m: LoteMovement) => {
    if (filters.userId[0] && m.requestedBy !== filters.userId[0]) return false;
    if (filters.from[0] && m.createdAt.slice(0, 10) < filters.from[0]) return false;
    if (filters.to[0] && m.createdAt.slice(0, 10) > filters.to[0]) return false;
    return true;
  });

  const filterDimensions: FilterDimension[] = [
    {
      key: 'type', label: ld.dataFilterType, options: typeOptions, selected: filters.type,
      onApply: (values) => setFilter('type', values), onClear: () => clearFilter('type'),
    },
    {
      key: 'userId', label: ld.dataFilterUser, options: userOptions, selected: filters.userId,
      onApply: (values) => setFilter('userId', values.slice(0, 1)), onClear: () => clearFilter('userId'),
    },
  ];

  if (loadingLote) return <Spinner size="small" label={ld.loading} />;
  if (!lote)       return <Text>{ld.notFound}</Text>;

  const lotStatusMap = t.field.lotStatus as Record<string, string>;

  // Fase B — historial de precio, aproximado por cultivo (ver i18n priceSubtitle).
  const priceEntries = lote.cultivo
    ? saleOrders
        .flatMap(so => so.items
          .filter(item => item.cropName?.toLowerCase() === lote.cultivo!.toLowerCase())
          .map(item => ({ date: so.orderDate, unitPrice: item.unitPrice })))
        .sort((a, b) => a.date.localeCompare(b.date))
    : [];
  const latestPrice = priceEntries[priceEntries.length - 1] ?? null;
  const priceMax = Math.max(...priceEntries.map(p => p.unitPrice), 0);
  const priceChartData = priceEntries.map(p => ({
    label: p.date.slice(5, 10),
    barHeightPct: priceMax > 0 ? (p.unitPrice / priceMax) * 100 : 0,
  }));

  // Finanzas: las tarjetas sin datos no se muestran (presupuesto, precio, costos).
  const hasFinanceData = latestBudget !== null || priceEntries.length > 0 || pieData.length > 0;

  const tabDefs: { value: LoteTab; label: string }[] = [
    { value: 'resumen',     label: ld.tabSummary },
    { value: 'mapa',        label: ld.tabMap },
    { value: 'actividades', label: ld.tabActivities },
    { value: 'finanzas',    label: ld.tabFinance },
  ];

  return (
    <div className={s.root}>
      <div className={s.header}>
        <Button icon={<ArrowLeft24Regular />} appearance="subtle" onClick={() => navigate(`/${tenantId}/campo`)} />
        <Text size={600} weight="semibold">{lote.name}</Text>
        <Badge color={lote.status === 'ACTIVE' ? 'success' : 'warning'} appearance="tint">
          {lotStatusMap[lote.status ?? ''] ?? lote.status ?? ''}
        </Badge>
        {costs && (
          <div className={s.headerCost}>
            <span className={s.metaLabel}>{ld.accCost}</span>
            <Text size={300} weight="semibold">{formatCurrency(costs.totalCost)}</Text>
          </div>
        )}
      </div>

      {topZones.length > 0 && (
        <div className={s.zoneList}>
          <span className={s.metaLabel}>{t.investorModules.childrenTitle}</span>
          {topZones.map(zone => (
            <div key={zone.moduleId} className={s.zoneGroup}>
              <Button
                appearance="outline"
                size="small"
                onClick={() => navigate(`/${tenantId}/fincas/modulo/${zone.moduleId}`)}
              >
                {zone.moduleCode}{zone.name ? ` · ${zone.name}` : ''}
              </Button>
              {loteModules.filter(m => m.parentModuleId === zone.moduleId).map(sub => (
                <Button
                  key={sub.moduleId}
                  appearance="subtle"
                  size="small"
                  onClick={() => navigate(`/${tenantId}/fincas/modulo/${sub.moduleId}`)}
                >
                  ↳ {sub.moduleCode}{sub.name ? ` · ${sub.name}` : ''}
                </Button>
              ))}
            </div>
          ))}
        </div>
      )}

      <SubTabs
        selected={activeTab}
        onSelect={selectTab}
        tabs={tabDefs}
      />

      {/* ── Resumen: KPIs del lote y riesgos de la parcela ───────────────── */}
      {activeTab === 'resumen' && <>
        {(lote.cultivo || lote.hectareas != null) && (
          <div className={s.meta}>
            {lote.cultivo && (
              <div className={s.metaItem}>
                <span className={s.metaLabel}>{ld.crop}</span>
                <Text size={300}>{lote.cultivo}</Text>
              </div>
            )}
            {lote.hectareas != null && (
              <div className={s.metaItem}>
                <span className={s.metaLabel}>{ld.hectares}</span>
                <Text size={300}>{lote.hectareas} ha</Text>
              </div>
            )}
          </div>
        )}

        {/* ── Panel de riesgos de la parcela (Fase B) ──────────────────────── */}
        <div className={s.card}>
          <Text weight="semibold">{ld.riskTitle}</Text>
          {loteRisks.length === 0 ? (
            <Text className={s.ndviPlaceholder}>{ld.riskNoData}</Text>
          ) : (
            loteRisks.map(a => (
              <div key={`${a.projectId}#${a.anomalyKey}`} className={s.riskRow} style={{ borderLeftColor: SEVERITY_BORDER[a.severity] }}>
                <div className={s.riskInfo}>
                  <span className={s.riskTitle}>{a.title}</span>
                  <span className={s.riskMeta}>{a.tipo}</span>
                </div>
                <Badge appearance="tint" size="small">{t.investorProduction[`severity${a.severity}` as keyof typeof t.investorProduction] ?? a.severity}</Badge>
              </div>
            ))
          )}
        </div>
      </>}

      {/* ── Mapa: capas NDVI real / Ortomosaico placeholder — Fase B ─────────── */}
      {/* Capa deshabilitada por configuración = no se ofrece. El ortomosaico aún
          no tiene pipeline ni ajuste que lo habilite, así que no se muestra. */}
      {activeTab === 'mapa' && ndviEnabledSetting !== false && (
      <div className={s.ndviCard}>
        <Text weight="semibold">{ld.mapLayersTitle}</Text>

        {mapLayer === 'ndvi' && <>
        {ndviDisabled && (
          <Text className={s.ndviPlaceholder} data-testid="ndvi-disabled-notice">{ld.ndviDisabled}</Text>
        )}

        {!ndviDisabled && loadingNdvi && (
          <Text className={s.ndviPlaceholder}>{ld.ndviLoading}</Text>
        )}

        {!ndviDisabled && !loadingNdvi && ndvi?.noBoundary && (
          <Text className={s.ndviPlaceholder}>{ld.ndviNoBoundary}</Text>
        )}

        {!ndviDisabled && !loadingNdvi && ndvi?.noApiKey && (
          <Text className={s.ndviPlaceholder}>{ld.ndviNoApiKey}</Text>
        )}

        {!ndviDisabled && !loadingNdvi && ndvi && !ndvi.noBoundary && !ndvi.noApiKey && (
          <>
            {ndvi.ndviStats ? (
              <div className={s.ndviStats}>
                <div className={s.ndviStatItem}>
                  <span className={s.ndviStatValue} style={{ color: ndviColor(ndvi.ndviStats.mean) }}>
                    {ndvi.ndviStats.mean.toFixed(2)}
                  </span>
                  <span className={s.ndviStatLabel}>{ld.ndviMean}</span>
                  <Text size={100} style={{ color: ndviColor(ndvi.ndviStats.mean) }}>
                    {ndviHealthLabel(ndvi.ndviStats.mean, ld as unknown as Record<string, string>)}
                  </Text>
                </div>
                <div className={s.ndviStatItem}>
                  <span className={s.ndviStatValue} style={{ color: tokens.colorNeutralForeground2 }}>
                    {ndvi.ndviStats.min.toFixed(2)}
                  </span>
                  <span className={s.ndviStatLabel}>{ld.ndviMin}</span>
                </div>
                <div className={s.ndviStatItem}>
                  <span className={s.ndviStatValue} style={{ color: tokens.colorNeutralForeground2 }}>
                    {ndvi.ndviStats.max.toFixed(2)}
                  </span>
                  <span className={s.ndviStatLabel}>{ld.ndviMax}</span>
                </div>
              </div>
            ) : (
              <Text className={s.ndviPlaceholder}>{ld.ndviNoData}</Text>
            )}

            {ndvi.imageUrl && (
              <img src={ndvi.imageUrl} alt={t.a11yLabels.ndviImage} className={s.ndviImage} />
            )}

            {(ndvi.date || ndvi.cloudCoverage != null) && (
              <div className={s.ndviBadge}>
                {ndvi.date && <span>{ld.ndviDate}: {ndvi.date}</span>}
                {ndvi.cloudCoverage != null && <span>☁ {ndvi.cloudCoverage}%</span>}
              </div>
            )}

            {ndviHistory && ndviHistory.history.length > 0 && (
              <div>
                <Text size={200} weight="semibold" style={{ display: 'block', marginBottom: '8px' }}>
                  {ld.ndviTimeline}
                </Text>
                <ResponsiveContainer width="100%" height={160}>
                  <LineChart data={ndviHistory.history} margin={{ top: 4, right: 8, bottom: 4, left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={tokens.colorNeutralStroke2} />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={fmtDayMonth} />
                    <YAxis domain={[0, 1]} tick={{ fontSize: 10 }} />
                    <Tooltip
                      formatter={(v: number) => v.toFixed(3)}
                      labelFormatter={(l: string) => `${t.common.date}: ${fmtDayMonth(l)}`}
                    />
                    <ReferenceLine y={0.5} stroke="#107c10" strokeDasharray="4 2" />
                    <ReferenceLine y={0.3} stroke="#ca5010" strokeDasharray="4 2" />
                    <Line type="monotone" dataKey="mean" stroke="#0078d4" strokeWidth={2}
                          dot={false} activeDot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        )}
        </>}
      </div>
      )}

      {/* ── Actividades: Datos del lote (filtros, feed y movimientos) ────────── */}
      {activeTab === 'actividades' && (
      <div className={s.section}>
        <Text weight="semibold">{ld.dataTitle}</Text>
        <FilterPanel
          dimensions={filterDimensions}
          dateRange={{
            label: ld.dataFilterDate,
            value: dateRangeValue,
            onApply: (v) => { setFilter('from', v.from ? [v.from] : []); setFilter('to', v.to ? [v.to] : []); },
            onClear: () => { clearFilter('from'); clearFilter('to'); },
          }}
          onClearAll={clearAll}
        />
        <ActivityFeed activities={activities} isLoading={loadingActs} loteId={loteId} />
        <LoteSystemActivity loteId={loteId!} />
        {filteredMovements.length > 0 && (
          <div className={s.movementsList}>
            {filteredMovements.map((m: LoteMovement) => (
              <div key={m.movementId} className={s.movementRow}>
                <Badge appearance="tint" color={m.type === 'IN' ? 'success' : 'warning'} size="small">
                  {ld.dataOriginMovement}
                </Badge>
                <Text size={200}>
                  {m.type === 'IN' ? '+' : '-'}{m.qty} {m.unit} — {m.reason} · {formatDate(m.createdAt.slice(0, 10))}
                </Text>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* ── Finanzas: presupuesto, costos por categoría, historial de precio ── */}
      {activeTab === 'finanzas' && <>
        {!hasFinanceData && (
          <Text className={s.ndviPlaceholder}>{ld.financeEmpty}</Text>
        )}

        {/* Presupuesto base de insumos */}
        {latestBudget && (
        <div className={s.card}>
          <Text weight="semibold">{ld.budgetTitle}</Text>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
            {ld.budgetProtocol}: {latestBudget.protocolName ?? '—'} &middot;{' '}
            {latestBudget.hectareas ?? '—'} ha &middot;{' '}
            {ld.budgetCycle}: {latestBudget.ciclo ?? '—'}
          </Text>
          <div>
            <div className={s.budgetRow}>
              <span>{ld.budgetChemicals}</span>
              <span>{formatCurrency((latestBudget.costoQuimicoHa ?? 0) * (latestBudget.hectareas ?? 1))}</span>
            </div>
            <div className={s.budgetRow}>
              <span>{ld.budgetFert}</span>
              <span>{formatCurrency((latestBudget.costoFertilizanteHa ?? 0) * (latestBudget.hectareas ?? 1))}</span>
            </div>
            <div className={s.budgetRow} style={{ color: tokens.colorNeutralForeground3, fontSize: '11px' }}>
              <span>{ld.budgetCostHaChem}</span>
              <span>{formatCurrency(latestBudget.costoQuimicoHa ?? 0)}</span>
            </div>
            <div className={s.budgetRow} style={{ color: tokens.colorNeutralForeground3, fontSize: '11px' }}>
              <span>{ld.budgetCostHaFert}</span>
              <span>{formatCurrency(latestBudget.costoFertilizanteHa ?? 0)}</span>
            </div>
            <div className={s.budgetTotal}>
              <span>{ld.budgetTotal}</span>
              <span style={{ color: tokens.colorBrandForeground1 }}>
                {formatCurrency(((latestBudget.costoQuimicoHa ?? 0) + (latestBudget.costoFertilizanteHa ?? 0)) * (latestBudget.hectareas ?? 1))}
              </span>
            </div>
          </div>
        </div>
        )}

        {/* Costos por categoría */}
        {pieData.length > 0 && (
        <div className={s.card}>
          <Text weight="semibold">{ld.costsByType}</Text>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                {pieData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v: number) => formatCurrency(v)} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
        )}

        {/* Historial de precio (Fase B) */}
        {priceEntries.length > 0 && (
        <div className={s.card}>
          <Text weight="semibold">{ld.priceTitle}</Text>
          <Text className={s.priceStat}>{priceSubtitle}</Text>
          {latestPrice && (
            <Text className={s.priceStat}>
              {ld.priceLatest}: <strong>{formatCurrency(latestPrice.unitPrice)}</strong> ({latestPrice.date.slice(0, 10)})
            </Text>
          )}
          <BarLineChart data={priceChartData} />
        </div>
        )}
      </>}
    </div>
  );
}
